import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { queryClient } from "@/lib/query-client";
import { appState, PageTransition } from "@fleet/ui";
import { cloneSession, isValidSession, queryKeys } from "@listto/shared";
import { readAccountsStateSync } from "@/lib/accounts";
import { resolveActiveAccountView } from "@/lib/active-account";
import { readTabAccountId } from "@/lib/tab-account";
import { SESSION_CHECK_TIMEOUT_MS } from "@/lib/constants";

/**
 * listto auth wrapper.
 *
 * NOTE (#13 — de-clone): listto deliberately does NOT use the shared
 * `AuthLayout` (the split brand-panel + glass card shell every clone inherited).
 * Instead each screen owns its own `AuthScene` — a single editorial stacked
 * column with the form full-bleed on the ambient background. So this route is a
 * thin wrapper: it only owns the "already authenticated → redirect" gate and the
 * page transition. The background, brand band and scroll container live in
 * `AuthScene`, rendered per-screen.
 */
function AuthLayoutRoute() {
  const location = useLocation();
  appState.markAsReady();

  return (
    <PageTransition routeKey={location().pathname}>
      <Outlet />
    </PageTransition>
  );
}

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  // If already authenticated, redirect to the app root.
  beforeLoad: async ({ search }) => {
    // Multi-account: `?mode=add` opens /login to ADD another account while the
    // current one stays signed in (Gmail style). The "already authenticated →
    // go home" gate must be skipped here; otherwise the switcher's "Add account"
    // would bounce straight back to the app and a 2nd login could never be added.
    const isAddingAccount = (search as { mode?: string }).mode === "add";
    if (isAddingAccount) {
      return;
    }

    // Per-tab gating: the "already authenticated → go home" gate must reflect
    // THIS tab's active account, NOT the global session machine (which holds the
    // last-authed real user). Without this, clicking "Sign in" from a GUEST tab
    // would bounce straight into the app as the global admin.
    //
    // The auth page runs in the SAME tab that navigated here, so this tab's own
    // pin IS the originating account — no need to recover it from the `redirect`
    // param, which the `/u/<n>` scheme required and which silently fell through
    // whenever the param was missing.
    //
    // LAYERING (boot safety): only take this branch when the tab has an EXPLICIT
    // pin — the faithful analogue of the old "the redirect param carried a
    // `/u/<n>`" condition. A tab with no pin yet (cold boot) falls through to the
    // baseline global-session fast path below, preserving every 401 / sign-out /
    // reload transition the BootManager depends on.
    const redirectTarget = (search as { redirect?: string }).redirect;
    const tabAccountId = readTabAccountId();
    if (tabAccountId !== null) {
      const view = resolveActiveAccountView(
        readAccountsStateSync(),
        tabAccountId,
        sessionActor.getSnapshot().context.session,
      );
      // Guest tab → show /login (do NOT redirect). Real account → bounce home.
      if (view.isAuthenticated) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: redirectTarget ?? "/" });
      }
      return;
    }

    // Fast path: check machine state before hitting the network.
    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
      if (snapshot.context.session) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
      }
      return;
    }

    // Network check — only if machine has no session yet.
    // Use api.GET (not fetchApi) so we can inspect the status without throwing.
    //
    // P23/CC-60 · I1-bis: this `await` must NOT be unbounded. A STALLED
    // get-session (held: neither a 401 nor a 5xx, which resolve quickly and therefore
    // phase 1 did not see it) left this `beforeLoad` unresolved, the router without
    // mounting the route, and `/login` with `document.body.innerText` EMPTY at
    // 3/6/10/15/20 s — measured in dev and on production bundle. The sibling
    // guard of `_app` already ran with a cap; this one was left without it because the
    // value was its own local literal. Now it comes from `lib/constants.ts`.
    //
    // The request is NOT canceled: it stays alive and synchronizes machine + cache when
    // it arrives. If a session turns out to exist, `BootManager` takes the user out of the
    // login screen; at most there is a flicker, never a blank screen.
    const fetchSession = async () =>
      api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
    type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

    const syncReply = (reply: FetchResult) => {
      const { data: replyData, error: replyError } = reply;
      // Synchronize the machine and the cache immediately.
      // This prevents the BootManager from seeing a "pending" or stale session
      // during the route transition, which causes redirection loops.
      sessionActor.send({
        type: "SERVER_REPLY",
        session: isValidSession(replyData) ? cloneSession(replyData) : null,
        error: replyError ?? null,
      });
      queryClient.setQueryData(queryKeys.auth.session(), replyData ?? null);
      return reply;
    };

    const inFlight = fetchSession()
      .then(syncReply)
      // Network error — let user try to log in anyway.
      .catch(() => null);

    const timedOut = new Promise<null>((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, SESSION_CHECK_TIMEOUT_MS);
    });

    const result = await Promise.race([inFlight, timedOut]);
    if (!result) {
      // Timeout or network error: render the login page. The late response remains
      // attached above, and BootManager redirects if it contains a session.
      return;
    }

    const { data } = result;

    // Redirect is thrown OUTSIDE the try/catch so it propagates to TanStack Router.
    // Better Auth returns HTTP 200 with a null body for unauthenticated requests,
    // but the OpenAPI schema types Session as non-null. Cast to match runtime reality.
    const sessionData = data as { user?: unknown } | null | undefined;
    if (sessionData?.user) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
    }
    // 401/403, network error, or null session — not authenticated, show login page.
  },
  component: AuthLayoutRoute,
});
